import { GameStateContextProvider } from 'app/src/game/GameStateContext';
import GameStateStore, { GameFinishedReason } from 'app/src/game/GameStateStore';
import { useLevelContext } from 'app/src/game/LevelContext';
import { useProgramStoreContext } from 'app/src/game/ProgramStoreContext';
import useLevelSequencer from 'app/src/game/useLevelSequencer';
import { LevelDescription } from 'app/src/LevelDescription';
import useForceUpdate from 'app/src/utils/useForceUpdate';
import useInitializer from 'app/src/utils/useInitializer';
import useValueChanged from 'app/src/utils/useValueChanged';
import * as React from 'react';

type Props = {
	children: React.ReactNode;
	levelDescription: LevelDescription;
};

export default function GameStateController({
	children,
	levelDescription,
}: Props): React.ReactElement | null {
	// Grab the robot's program. We'll feed these instructions to the game state
	// store as it executes.
	const { program } = useProgramStoreContext();

	// Build a new GameStateStore on first mount and keep it around until unmount.
	const gameStateStore = useInitializer(() => new GameStateStore(levelDescription), [
		levelDescription,
	]);

	// This is pretty hacky, but it works. Whenever we want to force an update of
	// this component, consumers can call forceUpdate(). This is necessary because
	// GameStateStore is a vanilla class and we don't have any bindings to its
	// data.
	const forceUpdate = useForceUpdate();

	// All updates to gameStateStore should trigger the instance of PubSub to
	// trigger a re-render of this component.
	React.useEffect(() => {
		gameStateStore.onUpdate.subscribe(forceUpdate);
		return (): void => gameStateStore.onUpdate.unsubscribe(forceUpdate);
	});

	// Enable sequencing on this gameStateStore.
	useLevelSequencer(gameStateStore, program);

	// Keep track of executing state and attempt count.
	const [executingProgram, setExecutingProgram] = React.useState(false);
	const [attempt, setAttempt] = React.useState(0);
	const [resets, setResets] = React.useState(0);

	// Create callbacks for starting/stopping.
	const reset = React.useCallback(() => {
		setExecutingProgram(false);
		setResets((a) => a + 1);
		gameStateStore.reset();
	}, [gameStateStore]);

	const start = React.useCallback(() => {
		setExecutingProgram(true);
		gameStateStore.advanceToNextPhase();
		setAttempt((a) => a + 1);
	}, [gameStateStore]);

	// When a user completes a level, tell the level controller.
	const { currentLevel, completeLevel } = useLevelContext();
	const allStarsCollected =
		gameStateStore.gameFinishedReason === GameFinishedReason.AllStarsCollected;
	const allStarsCollectedChanged = useValueChanged(allStarsCollected);
	React.useEffect(() => {
		if (allStarsCollectedChanged && allStarsCollected && currentLevel !== null) {
			completeLevel(currentLevel);
		}
	}, [currentLevel, completeLevel, allStarsCollectedChanged, allStarsCollected]);

	return (
		<GameStateContextProvider
			value={{ gameStateStore, attempt, resets, executingProgram, reset, start }}
		>
			{children}
		</GameStateContextProvider>
	);
}
